import React, { useMemo, useCallback, useEffect } from 'react';
import { Pagination } from 'antd';
import type { PaginationProps as AntPaginationProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { createCommand } from '../../base/actionParser';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

/** 设计器静态「每页条数」：pageSize 与 defaultPageSize 语义统一为同一默认值（与 store 空槽种入一致） */
function resolveDesignPageSize(propPageSize?: number, propDefaultPageSize?: number): number {
  const resolved = Number(propPageSize ?? propDefaultPageSize ?? 10);
  return Number.isFinite(resolved) && resolved > 0 ? resolved : 10;
}

export interface AndPaginationProps extends Omit<AntPaginationProps, 'onChange' | 'onShowSizeChange'>, BaseComponentProps, DataSetConfig {
  /** 页码变化动作 */
  changeAction?: string;
  /** 条数变化动作 */
  showSizeChangeAction?: string;
  /** 自定义 onChange */
  onChange?: (page: number, pageSize: number) => void;
  /** 自定义 onShowSizeChange */
  onShowSizeChange?: (current: number, size: number) => void;
}

export const AndPagination: React.FC<AndPaginationProps> = ({
  id, sessionId, develop, dataSet, dataSetField, 
  changeAction, showSizeChangeAction,
  onChange, onShowSizeChange,
  current: propCurrent, pageSize: propPageSize, total: propTotal,
  defaultPageSize: propDefaultPageSize,
  ...restProps
}) => {
  const addCommand = useAndStore((state) => state.addCommand);
  const setDataSet = useAndStore((state) => state.setDataSet);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  // 有 dataSet 时 field 为空则读/写整块对象（与 getDataSet/setDataSet 空 field 语义一致）
  const paginationField = dataSetField ?? (dataSetKey ? '' : undefined);
  // 稳定空对象引用，避免 selector 返回新 {} 导致无限重渲染（getSnapshot 需缓存）
  const emptyPagination = useMemo(() => ({}), []);
  // 从 store 订阅分页数据，实现双向绑定（Extend/AndTable 更新后组件会重渲染）
  const storePagination = useAndStore((s) => {
    if (!dataSetKey) return emptyPagination;
    const v = s.getDataSet(dataSetKey, paginationField ?? '');
    return (v != null && typeof v === 'object') ? v : emptyPagination;
  });

  const designPageSize = resolveDesignPageSize(propPageSize, propDefaultPageSize);
  const current = storePagination.current ?? propCurrent ?? 1;
  const pageSize = storePagination.pageSize ?? designPageSize;
  const total = storePagination.total ?? propTotal ?? 0;

  // 绑定 dataSet 时，将设计器配置的 pageSize/defaultPageSize 种入 store，避免 Extend/首帧请求仍用默认 10
  useEffect(() => {
    if (!dataSetKey || paginationField === undefined) return;
    const field = paginationField ?? '';
    const { getDataSet, setDataSet } = useAndStore.getState();
    const raw = getDataSet(dataSetKey, field);
    const seedPageSize = resolveDesignPageSize(propPageSize, propDefaultPageSize);

    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
      setDataSet(
        dataSetKey,
        field,
        {
          current: propCurrent ?? 1,
          pageSize: seedPageSize,
          total: propTotal ?? 0,
        },
        false
      );
      return;
    }

    const ps = Number(raw.pageSize);
    if (!(Number.isFinite(ps) && ps > 0)) {
      setDataSet(
        dataSetKey,
        field,
        {
          ...raw,
          pageSize: seedPageSize,
          current: raw.current ?? propCurrent ?? 1,
          total: raw.total ?? propTotal ?? 0,
        },
        false
      );
    }
  }, [
    dataSetKey,
    paginationField,
    propCurrent,
    propPageSize,
    propDefaultPageSize,
    propTotal,
  ]);

  // 页码变化处理
  const handleChange = useCallback((page: number, size: number) => {
    if (onChange) { onChange(page, size); return; }
    if (dataSetKey) {
      setDataSet(dataSetKey, paginationField ?? '', {
        ...storePagination,
        current: page,
        pageSize: size,
        total,
      });
    }
    if (changeAction && sessionId) {
      addCommand(sessionId, createCommand(changeAction, { page, pageSize: size }));
    }
  }, [onChange, dataSetKey, paginationField, setDataSet, storePagination, total, changeAction, sessionId, addCommand]);

  // 条数变化处理
  // rc-pagination 改 pageSize 时会先 onShowSizeChange 再 onChange；
  // 已配 changeAction 时由 onChange 统一发 command，避免与 showSizeChangeAction 双发。
  const handleShowSizeChange = useCallback((current: number, size: number) => {
    if (onShowSizeChange) { onShowSizeChange(current, size); return; }
    if (dataSetKey) {
      setDataSet(dataSetKey, paginationField ?? '', {
        ...storePagination,
        current,
        pageSize: size,
        total,
      });
    }
    if (changeAction) return;
    if (showSizeChangeAction && sessionId) {
      addCommand(sessionId, createCommand(showSizeChangeAction, { current, pageSize: size }));
    }
  }, [onShowSizeChange, dataSetKey, paginationField, setDataSet, storePagination, total, changeAction, showSizeChangeAction, sessionId, addCommand]);

  return (
    <Pagination 
      {...restProps} 
      current={current} 
      pageSize={pageSize} 
      total={total} 
      onChange={handleChange}
      onShowSizeChange={handleShowSizeChange}
      showTotal={useMemo(() => {
        const showTotalProp = (restProps as any).showTotal;
        if (typeof showTotalProp === 'string') {
          return (total: number, range: [number, number]) => 
            showTotalProp
              .replace('${total}', String(total))
              .replace('${range[0]}', String(range[0]))
              .replace('${range[1]}', String(range[1]))
              .replace('${range}', `${range[0]}-${range[1]}`);
        }
        return showTotalProp;
      }, [(restProps as any).showTotal])}
    />
  );
};

export default AndPagination;
